import React from "react";
import { Pressable, ScrollView, StyleSheet, View, type PressableStateCallbackType } from "react-native";
import {
  CaretDownIcon,
  CaretRightIcon,
  ChatCircleIcon,
  CheckCircleIcon,
  DotsThreeIcon,
  PlusIcon,
  StackSimpleIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { ModernizedActivityEntityType } from "@axio/graphql/generated/graphql";

import { PopoverMenu } from "../../../../../components/common/general/PopoverMenu/PopoverMenu";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import FilterPill from "./FilterPill";
import type {
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewEffectiveness,
  CourseEvaluatorReviewItemType,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
  CourseEvaluatorReviewSidebarMeta,
  CourseEvaluatorReviewSidebarTag,
} from "../types/reviewTypes";
import { COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS } from "../constants/itemTypeLabels";

interface ReviewSidebarProps {
  review: CourseEvaluatorReviewData;
  selectedItemId: string | null;
  filterMode: "all" | "undecided";
  collapsedSectionIds: Set<string>;
  commentedItemIds?: Set<string>;
  commentedItemCounts?: Map<string, number>;
  unresolvedItemCounts?: Map<string, number>;
  getDecision: (id: string) => CourseEvaluatorReviewDecision;
  onSelectItem: (itemId: string | null) => void;
  onToggleSection: (sectionId: string) => void;
  onFilterModeChange: (mode: "all" | "undecided") => void;
  onAddModule?: () => void;
  onDeleteManualActivity?: (item: CourseEvaluatorReviewSidebarItem) => void;
  deletableManualActivityIds?: ReadonlySet<string>;
  onAddActivity?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onAcceptGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onRejectGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
}

function getTagPresentation(tag: CourseEvaluatorReviewSidebarTag) {
  switch (tag) {
    case "instructional":
      return {
        label: "Instructional",
        backgroundColor: "#E6F3FF",
        textColor: "#0069CC",
      };
    case "lecture":
      return {
        label: "Lecture",
        backgroundColor: "#F5F0FE",
        textColor: "#7244BB",
      };
    case "supplementary":
      return {
        label: "Supplementary",
        backgroundColor: "#F2F4F7",
        textColor: "#344054",
      };
    case "new":
    default:
      return {
        label: "New",
        backgroundColor: "#E7F7F8",
        textColor: "#15727D",
      };
  }
}

function getItemTypePresentation(itemType: CourseEvaluatorReviewItemType) {
  switch (itemType) {
    case "assignment":
    case "project":
    case "moduleDescription":
      return {
        label: COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemType],
        color: itemType === "moduleDescription" ? "#7C3AED" : "#667085",
        icon: itemType === "moduleDescription" ? ("moduleDescription" as const) : undefined,
      };
    case "externalLink":
    case "discussion":
    case "quiz":
    case "file":
    case "resource":
    case "link":
    case "htmlContent":
    case "ile":
    case "checklist":
      return {
        label: COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemType],
        color: "#667085",
      };
    case "page":
    case "webpage":
      return {
        label: COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemType],
        color: "#D97706",
      };
    default:
      return {
        label: COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS.checklist,
        color: "#667085",
      };
  }
}

function getEffectivenessColor(effectiveness: CourseEvaluatorReviewEffectiveness) {
  switch (effectiveness) {
    case "excellent":
      return "#3B7C0F";
    case "fair":
      return "#B54708";
    case "poor":
      return "#D92D20";
    case "good":
    default:
      return "#3459D4";
  }
}

function DecisionIcon({ decision }: { decision: CourseEvaluatorReviewDecision }) {
  if (decision === "accepted") {
    return (
      <View style={styles.decisionIconBackground}>
        <CheckCircleIcon size={14} color={colors.success[400]} weight="bold" />
      </View>
    );
  }
  if (decision === "rejected") {
    return (
      <View style={styles.decisionIconBackground}>
        <XCircleIcon size={14} color={colors.error[500]} weight="bold" />
      </View>
    );
  }
  if (decision === "synced") {
    return (
      <View style={styles.decisionIconBackground}>
        <CheckCircleIcon size={14} color={colors.brand[400]} weight="bold" />
      </View>
    );
  }
  return <View style={styles.sidebarRadio} />;
}

function getItemBorderColor(decision: CourseEvaluatorReviewDecision) {
  if (decision === "accepted") return colors.success[500];
  if (decision === "rejected") return colors.error[500];
  if (decision === "synced") return colors.brand[600];
  return "transparent";
}

function renderMeta(itemId: string, meta: CourseEvaluatorReviewSidebarMeta, index: number) {
  if (meta.kind === "tag") {
    const tag = getTagPresentation(meta.tag);

    return (
      <View
        key={`${itemId}-tag-${index}`}
        style={[
          styles.sidebarPill,
          {
            backgroundColor: tag.backgroundColor,
          },
        ]}>
        <CustomText
          text={tag.label}
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={{ color: tag.textColor }}
        />
      </View>
    );
  }

  if (meta.kind === "itemType") {
    const itemType = getItemTypePresentation(meta.itemType);

    return (
      <View key={`${itemId}-item-type-${index}`} style={styles.sidebarFooterTextRow}>
        {itemType.icon === "moduleDescription" && <StackSimpleIcon size={10} color={itemType.color} weight="bold" />}
        <CustomText
          text={itemType.label}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={[styles.sidebarFooterText, { color: itemType.color }]}
        />
      </View>
    );
  }

  if (meta.kind === "manual") {
    return (
      <View key={`${itemId}-manual-${index}`} style={styles.manualPill}>
        <CustomText text="MANUAL" useTranslationText={false} size="xs" weight="medium" style={styles.manualMetaText} />
      </View>
    );
  }

  return (
    <View key={`${itemId}-effectiveness-${index}`} style={styles.sidebarFooterTextRow}>
      <CustomText
        text={meta.effectiveness.charAt(0).toUpperCase() + meta.effectiveness.slice(1)}
        useTranslationText={false}
        size="xs"
        weight="normal"
        style={[styles.sidebarFooterText, { color: getEffectivenessColor(meta.effectiveness) }]}
      />
    </View>
  );
}

function isManualItem(item: CourseEvaluatorReviewSidebarItem): boolean {
  return item.meta?.some((meta) => meta.kind === "manual") === true;
}

function getManualItemType(item: CourseEvaluatorReviewSidebarItem): CourseEvaluatorReviewItemType | undefined {
  const itemTypeMeta = item.meta?.find(
    (meta): meta is Extract<CourseEvaluatorReviewSidebarMeta, { kind: "itemType" }> => meta.kind === "itemType"
  );

  return itemTypeMeta?.itemType;
}

function renderManualMeta(item: CourseEvaluatorReviewSidebarItem) {
  const itemType = getManualItemType(item);
  const newTag = item.meta?.find(
    (meta): meta is Extract<CourseEvaluatorReviewSidebarMeta, { kind: "tag" }> =>
      meta.kind === "tag" && meta.tag === "new"
  );

  return (
    <View style={styles.sidebarItemMeta}>
      {newTag ? renderMeta(item.id, newTag, 0) : null}
      <View style={styles.manualPill}>
        <CustomText text="MANUAL" useTranslationText={false} size="xs" weight="medium" style={styles.manualMetaText} />
      </View>
      {itemType ? (
        <CustomText
          text={COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemType]}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.manualItemTypeText}
        />
      ) : null}
    </View>
  );
}

function ReviewSidebar({
  review,
  selectedItemId,
  filterMode,
  collapsedSectionIds,
  commentedItemIds,
  unresolvedItemCounts,
  commentedItemCounts,
  getDecision,
  onSelectItem,
  onToggleSection,
  onFilterModeChange,
  onAddModule,
  onDeleteManualActivity,
  deletableManualActivityIds,
  onAddActivity,
  onAcceptGroup,
  onRejectGroup,
}: ReviewSidebarProps) {
  return (
    <View style={styles.sidebar}>
      <Pressable
        onPress={() => onSelectItem(null)}
        role="button"
        accessibilityRole="button"
        accessibilityLabel="Select overview"
        style={({ hovered }: PressableStateCallbackType) => [
          styles.overviewButton,
          selectedItemId === null && styles.overviewButtonActive,
          hovered && selectedItemId !== null && styles.sidebarItemHover,
        ]}>
        <CustomText text="Overview" useTranslationText={false} size="s" weight="bold" style={styles.overviewLabel} />
        <View style={styles.overviewScoreWrap}>
          <CustomText
            text={`${review.score}`}
            useTranslationText={false}
            size="xs"
            weight="bold"
            style={styles.overviewScore}
          />
          <CustomText
            text={`/${review.maxScore}`}
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={styles.overviewScoreSuffix}
          />
        </View>
      </Pressable>

      <View style={styles.sidebarHeader}>
        <CustomText text="Modules" useTranslationText={false} size="s" weight="bold" style={styles.sidebarHeaderText} />
        <View style={styles.filterPills}>
          <FilterPill label="All" isActive={filterMode === "all"} onPress={() => onFilterModeChange("all")} />
          <FilterPill
            label="Undecided"
            isActive={filterMode === "undecided"}
            onPress={() => onFilterModeChange("undecided")}
          />
        </View>
      </View>

      {onAddModule ? (
        <Pressable
          onPress={onAddModule}
          role="button"
          accessibilityRole="button"
          accessibilityLabel="Add module"
          style={({ hovered }: PressableStateCallbackType) => [
            styles.addModuleButton,
            hovered && styles.addModuleButtonHover,
          ]}>
          <PlusIcon size={14} color={colors.grey[600]} weight="bold" />
          <CustomText
            text="Add module"
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={styles.addModuleText}
          />
        </Pressable>
      ) : null}

      <ScrollView
        style={styles.sidebarScroll}
        contentContainerStyle={styles.sidebarScrollContent}
        tabIndex={0}
        showsVerticalScrollIndicator>
        {review.sidebarGroups.map((group) => {
          const isCollapsed = collapsedSectionIds.has(group.id);
          const decidedInGroup = group.decided;

          return (
            <View key={group.id} style={styles.sidebarSection}>
              <View style={styles.sidebarSectionHeader}>
                <Pressable
                  onPress={() => onToggleSection(group.id)}
                  role="button"
                  accessibilityRole="button"
                  accessibilityLabel={`${isCollapsed ? "Expand" : "Collapse"} ${group.title}`}
                  style={styles.sidebarSectionToggle}>
                  {isCollapsed ? (
                    <CaretRightIcon size={14} color={colors.grey[700]} />
                  ) : (
                    <CaretDownIcon size={14} color={colors.grey[700]} />
                  )}
                  <CustomText
                    text={group.title}
                    useTranslationText={false}
                    size="xs"
                    weight="bold"
                    style={styles.sectionTitle}
                  />
                  <CustomText
                    text={`${decidedInGroup}/${group.total}`}
                    useTranslationText={false}
                    size="xs"
                    weight="normal"
                    style={decidedInGroup === group.total ? styles.sectionCountDone : styles.sectionCount}
                  />
                </Pressable>
                <PopoverMenu
                  triggerAccessibilityLabel={`${group.title} section options`}
                  triggerStyle={styles.sectionDots}
                  menuWidth={128}
                  items={[
                    ...(group.id !== "course-level-content" && onAddActivity
                      ? [
                          {
                            key: "add-activity",
                            label: "Add activity",
                            onPress: () => onAddActivity(group),
                          },
                        ]
                      : []),
                    {
                      key: "accept-all",
                      label: "Accept all",
                      onPress: () => onAcceptGroup?.(group),
                    },
                    {
                      key: "skip-all",
                      label: "Skip all",
                      danger: true,
                      onPress: () => onRejectGroup?.(group),
                    },
                  ]}
                  renderTrigger={() => <DotsThreeIcon size={16} color={colors.grey[500]} weight="bold" />}
                />
              </View>

              {!isCollapsed ? (
                <>
                  {group.items.map((item) => {
                    const decision = getDecision(item.id);
                    const borderColor = getItemBorderColor(decision);
                    const manualItem = isManualItem(item);
                    const canDeleteAddition =
                      (item.entityType === ModernizedActivityEntityType.Assignment ||
                        item.entityType === ModernizedActivityEntityType.Project ||
                        item.entityType === ModernizedActivityEntityType.OtherActivity ||
                        item.entityType === ModernizedActivityEntityType.IleRecommendation) &&
                      deletableManualActivityIds?.has(item.id) === true &&
                      Boolean(onDeleteManualActivity);
                    const commentCount = commentedItemCounts?.get(item.id) ?? 0;
                    const unresolvedCount = unresolvedItemCounts?.get(item.id) ?? 0;
                    const hasComments = (commentedItemIds?.has(item.id) ?? false) && commentCount > 0;
                    const allResolved = hasComments && unresolvedCount === 0;

                    return (
                      <Pressable
                        key={item.id}
                        onPress={() => onSelectItem(item.id)}
                        role="button"
                        accessibilityRole="button"
                        accessibilityLabel={`Select ${item.title}`}
                        style={({ hovered }: PressableStateCallbackType) => [
                          styles.sidebarItem,
                          { borderLeftWidth: 2, borderLeftColor: borderColor },
                          selectedItemId === item.id && styles.sidebarItemActive,
                          hovered && selectedItemId !== item.id && styles.sidebarItemHover,
                        ]}>
                        <View style={styles.sidebarItemRow}>
                          <DecisionIcon decision={decision} />
                          <View style={styles.sidebarItemContent}>
                            <CustomText
                              text={item.title}
                              useTranslationText={false}
                              size="xs"
                              weight="normal"
                              style={styles.sidebarItemTitle}
                            />
                            {manualItem ? renderManualMeta(item) : null}
                            {!manualItem && item.meta?.length ? (
                              <View style={styles.sidebarItemMeta}>
                                {item.meta.map((meta, index) => renderMeta(item.id, meta, index))}
                              </View>
                            ) : null}
                          </View>
                          {hasComments && (
                            <View style={[styles.commentMarkerWrap, allResolved && styles.commentMarkerResolved]}>
                              <ChatCircleIcon
                                size={14}
                                color={allResolved ? "#6B7280" : "#a855f7"}
                                weight="fill"
                                style={styles.commentMarkerIcon}
                              />
                              <CustomText
                                text={`${allResolved ? commentCount : unresolvedCount}`}
                                useTranslationText={false}
                                size="xs"
                                weight="medium"
                                style={[styles.commentMarkerCount, allResolved && styles.commentMarkerCountResolved]}
                              />
                            </View>
                          )}
                          {decision === "synced" && (
                            <View style={styles.publishedPill}>
                              <CustomText
                                text="Published"
                                useTranslationText={false}
                                size="xs"
                                weight="bold"
                                style={styles.publishedPillText}
                              />
                            </View>
                          )}
                          {canDeleteAddition ? (
                            <PopoverMenu
                              triggerAccessibilityLabel={`${item.title} options`}
                              triggerStyle={styles.itemDots}
                              menuWidth={144}
                              items={[
                                {
                                  key: "delete-addition",
                                  label: "Delete addition",
                                  danger: true,
                                  onPress: () => onDeleteManualActivity?.(item),
                                },
                              ]}
                              renderTrigger={() => <DotsThreeIcon size={16} color={colors.grey[500]} weight="bold" />}
                            />
                          ) : null}
                        </View>
                      </Pressable>
                    );
                  })}

                  {group.id !== "course-level-content" && onAddActivity ? (
                    <Pressable
                      onPress={() => onAddActivity(group)}
                      role="button"
                      accessibilityRole="button"
                      accessibilityLabel={`Add activity to ${group.title}`}
                      style={({ hovered }: PressableStateCallbackType) => [
                        styles.addActivityButton,
                        hovered && styles.addActivityButtonHover,
                      ]}>
                      <PlusIcon size={14} color={colors.grey[600]} weight="regular" />
                      <CustomText
                        text="Add activity"
                        useTranslationText={false}
                        size="xs"
                        weight="normal"
                        style={styles.addActivityText}
                      />
                    </Pressable>
                  ) : null}
                </>
              ) : null}
            </View>
          );
        })}
      </ScrollView>

      <View style={styles.sidebarFooter}>
        <CustomText
          text={`${review.queuedCount} queued`}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.queuedText}
        />
        <CustomText
          text={`${review.skippedCount} skipped`}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.skippedText}
        />
        {review.publishedCount > 0 && (
          <CustomText
            text={`${review.publishedCount} published`}
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={styles.publishedText}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  sidebar: {
    width: 350,
    backgroundColor: "#FFFFFF",
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
    flexDirection: "column",
    minHeight: 0,
  },
  overviewButton: {
    minHeight: 44,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  overviewButtonActive: {
    backgroundColor: "#F5F0FE",
  },
  overviewLabel: {
    color: colors.grey[900],
  },
  overviewScoreWrap: {
    flexDirection: "row",
    alignItems: "baseline",
  },
  overviewScore: {
    color: colors.grey[900],
  },
  overviewScoreSuffix: {
    color: colors.grey[500],
  },
  sidebarHeader: {
    minHeight: 38,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sidebarHeaderText: {
    color: colors.grey[700],
  },
  filterPills: {
    flexDirection: "row",
    gap: 4,
  },
  addModuleButton: {
    minHeight: 32,
    marginHorizontal: 8,
    marginVertical: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: colors.grey[50],
  },
  addModuleButtonHover: {
    backgroundColor: colors.grey[100],
  },
  addModuleText: {
    color: colors.grey[600],
  },
  sidebarScroll: {
    flex: 1,
    minHeight: 0,
    overflow: "scroll",
  },
  sidebarScrollContent: {
    paddingBottom: 16,
    flexGrow: 1,
  },
  sidebarSection: {
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  sidebarSectionHeader: {
    minHeight: 39,
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sidebarSectionToggle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  sectionTitle: {
    color: colors.grey[700],
  },
  sectionCount: {
    color: colors.grey[500],
  },
  sectionCountDone: {
    color: colors.success[600],
  },
  sectionDots: {
    marginLeft: 12,
    padding: 4,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  itemDots: {
    padding: 2,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  sidebarItem: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginHorizontal: 8,
    borderRadius: 8,
  },
  sidebarItemActive: {
    backgroundColor: "#F5F0FE",
  },
  sidebarItemHover: {
    backgroundColor: colors.grey[50],
  },
  addActivityButtonHover: {
    backgroundColor: colors.grey[50],
  },
  addActivityText: {
    color: colors.grey[600],
  },
  sidebarItemRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  sidebarItemContent: {
    flex: 1,
    minWidth: 0,
  },
  sidebarRadio: {
    width: 14,
    height: 14,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: colors.grey[300],
    backgroundColor: "#FFFFFF",
  },
  decisionIconBackground: {
    width: 14,
    height: 14,
    borderRadius: 999,
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
  },
  sidebarItemTitle: {
    color: colors.grey[700],
    lineHeight: 18,
  },
  commentMarkerWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },
  commentMarkerIcon: {
    flexShrink: 0,
  },
  commentMarkerCount: {
    color: "#a855f7",
    fontSize: 11,
    lineHeight: 14,
  },
  commentMarkerResolved: {
    opacity: 0.6,
  },
  commentMarkerCountResolved: {
    color: "#6B7280",
  },
  publishedPill: {
    paddingHorizontal: 10,
    height: 21,
    justifyContent: "center",
    borderRadius: 9999,
    backgroundColor: "#CCD7FF",
  },
  publishedPillText: {
    color: "#000B33",
    fontSize: 10,
  },
  sidebarItemMeta: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginLeft: 0,
    marginTop: 2,
    flexWrap: "wrap",
  },
  addActivityButton: {
    minHeight: 32,
    marginHorizontal: 8,
    marginTop: 4,
    marginBottom: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: colors.white,
  },
  sidebarPill: {
    borderRadius: 8,
    paddingHorizontal: 8,
    justifyContent: "center",
    height: 24,
  },
  manualPill: {
    borderRadius: 8,
    paddingHorizontal: 8,
    justifyContent: "center",
    height: 24,
    backgroundColor: "#F5F0FE",
  },
  sidebarFooterTextRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  sidebarFooterText: {
    fontSize: 11,
    lineHeight: 16,
  },
  manualMetaText: {
    color: "#7244BB",
    fontSize: 10,
    letterSpacing: 0.4,
  },
  manualItemTypeText: {
    color: "#667085",
    fontSize: 11,
    lineHeight: 16,
  },
  sidebarFooter: {
    minHeight: 34,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  queuedText: {
    color: "#3F7F18",
    fontSize: 11,
    lineHeight: 16,
  },
  skippedText: {
    color: "#D92D20",
    fontSize: 11,
    lineHeight: 16,
  },
  publishedText: {
    color: colors.brand[600],
    fontSize: 11,
    lineHeight: 16,
  },
});

export default ReviewSidebar;
